// src/pages/admin/GameJamList.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  doc as firestoreDoc,
  getDoc,
  updateDoc,
  deleteField
} from 'firebase/firestore';
import { db } from '../../firebase';

const awardLabels = ['대상', '최우수상', '우수상', '우수상'];

export default function GameJamList() {
  const [grouped, setGrouped] = useState({});           // { year: [{ id, title }] }
  const [editing, setEditing] = useState(null);         // { year, idx }
  const [newId, setNewId] = useState('');
  const [collapsed, setCollapsed] = useState({});       // { year: true/false }
  const navigate = useNavigate();

  // load data
  useEffect(() => {
    (async () => {
      const snap = await getDoc(firestoreDoc(db, 'data', 'gamejam'));
      if (!snap.exists()) return setGrouped({});
      const data = snap.data();
      const temp = {};
      for (const [key, ids] of Object.entries(data)) {
        if (!Array.isArray(ids)) continue;
        const year = Number(key.replace('gamejam',''));
        const arr = await Promise.all(ids.map(async id => {
          if (!id) return { id: '', title: '' };
          const g = await getDoc(firestoreDoc(db,'games',id));
          return { id, title: g.exists() ? g.data().title : '—' };
        }));
        temp[year] = arr;
      }
      setGrouped(temp);
    })();
  }, []);

  const startEdit = (year, idx) => {
    setEditing({ year, idx });
    setNewId(grouped[year][idx].id);
  };

  const confirmEdit = async () => {
    const { year, idx } = editing;
    const key = `gamejam${year}`;
    const ref = firestoreDoc(db,'data','gamejam');
    const current = grouped[year].map(i=>i.id);
    const updated = current.map((id,i)=> i===idx? newId : id);
    await updateDoc(ref,{ [key]: updated });
    let title = '';
    if (newId) {
      const g = await getDoc(firestoreDoc(db,'games',newId));
      title = g.exists()? g.data().title : '—';
    }
    setGrouped(g => ({
      ...g,
      [year]: g[year].map((item,i)=> i===idx? { id:newId, title } : item)
    }));
    setEditing(null);
    setNewId('');
  };

  const deleteYear = async year => {
    if (!window.confirm(`${year}년 게임잼 전체를 삭제하시겠습니까?`)) return;
    const key = `gamejam${year}`;
    const ref = firestoreDoc(db,'data','gamejam');
    await updateDoc(ref,{ [key]: deleteField() });
    setGrouped(g => {
      const { [year]:_, ...rest } = g;
      return rest;
    });
  };

  const years = Object.keys(grouped)
    .map(Number)
    .sort((a,b)=> b-a);

  const toggleCollapse = year => {
    setCollapsed(c => ({ ...c, [year]: !c[year] }));
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">게임잼 관리 대시보드</h1>
        <button
          onClick={()=> navigate('/admin/gamejam/create')}
          className="px-4 py-2 border border-green-600 text-green-600 rounded hover:bg-green-50"
        >
          신규 등록
        </button>
      </div>

      {years.map(year => {
        const list = grouped[year] || [];
        const isCollapsed = !!collapsed[year];
        return (
          <section key={year} className="bg-neutral-800 shadow">
            <div className="flex justify-between items-center px-4 py-2 bg-neutral-700">
              <button
                onClick={()=> toggleCollapse(year)}
                className="flex items-center space-x-2 text-white"
              >
                <span className="text-lg font-semibold">{year}년</span>
                <span className="text-xl">
                  {isCollapsed ? '▸' : '▾'}
                </span>
              </button>
              <button
                onClick={()=> deleteYear(year)}
                className="px-2 py-1 border border-red-600 text-red-600 rounded hover:bg-red-50 text-sm"
              >
                삭제
              </button>
            </div>

            {!isCollapsed && (
              <div className="overflow-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-neutral-700 text-left">
                      <th className="p-3">수상구분</th>
                      <th className="p-3">게임 타이틀</th>
                      <th className="p-3">게임 ID</th>
                      <th className="p-3 text-center">수정</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((item,idx)=> {
                      const isEd = editing?.year===year && editing?.idx===idx;
                      const dispId = item.id||'—';
                      const dispTitle = item.id? item.title||'—':'—';
                      return (
                        <tr key={idx} className="border-t border-neutral-600">
                          <td className="p-3 text-white">{awardLabels[idx]}</td>
                          <td className="p-3 text-white">
                            {isEd? '–' : dispTitle}
                          </td>
                          <td className="p-3">
                            {isEd ? (
                              <input
                                type="text"
                                value={newId}
                                onChange={e=>setNewId(e.target.value)}
                                className="w-full bg-neutral-700 text-white border border-neutral-600 rounded p-1 text-sm"
                              />
                            ) : (
                              <span className="text-gray-300">{dispId}</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {isEd ? (
                              <button
                                onClick={confirmEdit}
                                className="px-2 py-1 border border-green-600 text-green-600 rounded hover:bg-green-50 text-sm"
                              >
                                확인
                              </button>
                            ) : (
                              <button
                                onClick={()=> startEdit(year, idx)}
                                className="px-2 py-1 border border-blue-600 text-blue-600 rounded hover:bg-blue-50 text-sm"
                              >
                                수정
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {list.length===0 && (
                      <tr>
                        <td colSpan="4" className="p-4 text-center text-gray-500">
                          해당 연도에 등록된 게임잼이 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        );
      })}

      {years.length===0 && (
        <p className="text-center text-gray-500">등록된 게임잼이 없습니다.</p>
      )}
    </div>
  );
}
